import type { CardData } from '@/types/payment'

interface LegacyPagarMeCreditCard {
  cardNumber: string
  cardHolderName: string
  cardExpirationMonth: string
  cardExpirationYear: string
  cardCVV: string
  fieldErrors?: () => Record<string, string>
  generateHash: (callback: (cardHash: string) => void) => void
}

interface LegacyPagarMeGlobal {
  encryption_key?: string
  creditCard?: new () => LegacyPagarMeCreditCard
  client?: {
    connect?: (params: Record<string, string>) => Promise<{
      security: {
        encrypt: (payload: Record<string, string>) => Promise<string>
      }
    }>
  }
}

declare global {
  interface Window {
    PagarMe?: LegacyPagarMeGlobal
  }
}

const PAGARME_SCRIPT_URL = 'https://assets.pagar.me/js/pagarme.min.js'
const PAGARME_V5_TOKEN_URL = 'https://api.pagar.me/core/v5/tokens'
const MUNDIPAGG_TOKEN_URL = 'https://api.mundipagg.com/core/v1/tokens'

function getPagarmeClientKey(): string {
  return (
    import.meta.env.VITE_PAGARME_ENCRYPTION_KEY ||
    import.meta.env.VITE_PAGARME_PUBLIC_KEY ||
    ''
  ).trim()
}

function getPagarmeTokenizeBaseUrl(): string {
  const envUrl = (
    import.meta.env.VITE_PAGARME_TOKENIZE_URL ||
    ''
  ).trim()

  return envUrl || PAGARME_V5_TOKEN_URL
}

function deduplicateUrls(urls: string[]): string[] {
  return [...new Set(urls.filter(Boolean))]
}

function normalizeExpirationYear(year: string): number {
  const digitsOnly = year.replace(/\D/g, '')
  if (digitsOnly.length === 0) {
    return 0
  }

  if (digitsOnly.length >= 4) {
    return Number(digitsOnly.slice(0, 4))
  }

  // MM/AA -> converte para ano completo do século atual.
  const shortYear = Number(digitsOnly.slice(0, 2))
  return 2000 + shortYear
}

function buildTokenizationErrorMessage(errorPayload: any): string {
  const message = typeof errorPayload?.message === 'string' ? errorPayload.message : ''

  const firstErrorArray = errorPayload?.errors
    ? Object.values(errorPayload.errors).find((value) => Array.isArray(value) && value.length > 0)
    : null

  const firstError = Array.isArray(firstErrorArray) ? String(firstErrorArray[0] || '') : ''

  if (firstError) {
    return `${message || 'Erro na tokenização do cartão'}: ${firstError}`
  }

  return message || 'Não foi possível tokenizar o cartão no Pagar.me.'
}

async function tokenizeCardViaApi(publicKey: string, cardData: CardData, allowFallback = true): Promise<string> {
  const [expMonth = '', expYear = ''] = cardData.card_expiration_date.split('/')
  const monthNumber = Number(expMonth.replace(/\D/g, ''))
  const yearNumber = normalizeExpirationYear(expYear)

  const payload = {
    type: 'card',
    card: {
      number: cardData.card_number.replace(/\D/g, ''),
      holder_name: cardData.card_holder_name,
      exp_month: monthNumber,
      exp_year: yearNumber,
      cvv: cardData.card_cvv,
    },
  }

  const candidateUrls = allowFallback ? deduplicateUrls([
    getPagarmeTokenizeBaseUrl(),
    MUNDIPAGG_TOKEN_URL,
  ]) : [PAGARME_V5_TOKEN_URL]

  let lastError = 'Não foi possível tokenizar o cartão no Pagar.me.'

  for (const baseUrl of candidateUrls) {
    const separator = baseUrl.includes('?') ? '&' : '?'
    const requestUrl = `${baseUrl}${separator}appId=${encodeURIComponent(publicKey)}`

    try {
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const responseData = await response.json().catch(() => null)

      if (!response.ok) {
        lastError = buildTokenizationErrorMessage(responseData)
        continue
      }

      const token = String(responseData?.id || '').trim()
      if (token) {
        return token
      }

      lastError = 'Tokenização concluída sem retornar ID do token.'
    } catch (error) {
      lastError = error instanceof Error ? error.message : 'Falha de rede na tokenização do cartão.'
    }
  }

  throw new Error(lastError)
}

function getTokenizationErrorMessage(errors: Record<string, string>): string {
  const firstError = Object.values(errors)[0]
  return firstError || 'Dados do cartão inválidos.'
}

export const pagarmeService = {
  /**
   * Inicializar SDK do Pagar.me
   */
  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.PagarMe) {
        resolve()
        return
      }

      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${PAGARME_SCRIPT_URL}"]`)
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true })
        existingScript.addEventListener('error', () => reject(new Error('Erro ao carregar SDK do Pagar.me')), { once: true })
        return
      }

      const script = document.createElement('script')
      script.src = PAGARME_SCRIPT_URL
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Erro ao carregar SDK do Pagar.me'))
      document.head.appendChild(script)
    })
  },

  /**
   * Tokenizar cartão de crédito (client-side)
   */
  async tokenizeCard(cardData: CardData, publicKey?: string): Promise<string> {
    if (publicKey !== undefined) {
      if (!publicKey.startsWith('pk_')) {
        throw new Error('Pagamento com cartão indisponível no momento.')
      }
      return tokenizeCardViaApi(publicKey, cardData, false)
    }
    const encryptionKey = getPagarmeClientKey()
    if (!encryptionKey) {
      throw new Error('Chave do Pagar.me não configurada no checkout.')
    }

    // Fluxo recomendado para chaves públicas (pk_*): tokeniza direto no endpoint v5.
    if (encryptionKey.startsWith('pk_')) {
      return tokenizeCardViaApi(encryptionKey, cardData)
    }

    await this.init()

    const sdk = window.PagarMe
    if (!sdk) {
      throw new Error('SDK do Pagar.me não carregou corretamente.')
    }

    const [expMonth = '', expYear = ''] = cardData.card_expiration_date.split('/')

    // Fluxo moderno do SDK legado (quando disponível)
    if (sdk.client?.connect) {
      const client = await sdk.client.connect({ encryption_key: encryptionKey })
      return client.security.encrypt({
        card_number: cardData.card_number.replace(/\s/g, ''),
        card_holder_name: cardData.card_holder_name,
        card_expiration_date: `${expMonth}${expYear}`,
        card_cvv: cardData.card_cvv,
      })
    }

    // Fallback para o objeto clássico exposto por assets.pagar.me/js/pagarme.min.js
    if (!sdk.creditCard) {
      throw new Error('SDK do Pagar.me incompatível: método de tokenização não encontrado.')
    }

    const card = new sdk.creditCard()
    card.cardNumber = cardData.card_number.replace(/\D/g, '')
    card.cardHolderName = cardData.card_holder_name
    card.cardExpirationMonth = expMonth
    card.cardExpirationYear = expYear
    card.cardCVV = cardData.card_cvv

    const fieldErrors = card.fieldErrors?.() || {}
    if (Object.keys(fieldErrors).length > 0) {
      throw new Error(getTokenizationErrorMessage(fieldErrors))
    }

    sdk.encryption_key = encryptionKey

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(
          new Error(
            'Falha ao tokenizar cartão no Pagar.me. Verifique a chave pública/encryption_key e o domínio liberado.'
          )
        )
      }, 12000)

      try {
        card.generateHash((cardHash: string) => {
          window.clearTimeout(timeout)
          if (!cardHash || typeof cardHash !== 'string') {
            reject(new Error('Não foi possível gerar token do cartão.'))
            return
          }

          resolve(cardHash)
        })
      } catch (error) {
        window.clearTimeout(timeout)
        reject(error instanceof Error ? error : new Error('Erro inesperado ao tokenizar cartão.'))
      }
    })
  },

  /**
   * Validar número do cartão
   */
  validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\D/g, '')
    if (cleaned.length < 13 || cleaned.length > 19) {
      return false
    }

    // Algoritmo de Luhn
    let sum = 0
    let isEven = false

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10)

      if (isEven) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      isEven = !isEven
    }

    return sum % 10 === 0
  },

  /**
   * Detectar bandeira do cartão
   */
  getCardBrand(cardNumber: string): string | null {
    const cleaned = cardNumber.replace(/\D/g, '')

    const brands: Record<string, RegExp> = {
      visa: /^4/,
      mastercard: /^5[1-5]/,
      amex: /^3[47]/,
      elo: /^(4011|4312|4389|4514|4576|5041|5066|5067|6277|6362|6363|6504|6505|6516)/,
      hipercard: /^(38|60)/,
      diners: /^(301|305|36|38)/,
      discover: /^6(?:011|5)/,
    }

    for (const [brand, regex] of Object.entries(brands)) {
      if (regex.test(cleaned)) {
        return brand
      }
    }

    return null
  },

  /**
   * Formatar número do cartão
   */
  formatCardNumber(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\D/g, '')
    const groups = cleaned.match(/.{1,4}/g) || []
    return groups.join(' ')
  },

  /**
   * Formatar data de expiração (MM/AA)
   */
  formatExpirationDate(value: string): string {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`
    }
    return cleaned
  },
}
